import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, FolderKanban, BellRing, Bot, Scale, ListChecks,
    FileText, CreditCard, Settings as Cog, LifeBuoy, Mountain, Menu, LogOut,
    Search, ShieldAlert, ExternalLink, Bell, Eye, Radar, Map, Stethoscope,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-dashboard" },
    { to: "/processos", label: "Meus Processos", icon: FolderKanban, testid: "sidebar-processos" },
    { to: "/inteligencia", label: "Inteligência Mineral", icon: Radar, testid: "sidebar-inteligencia" },
    { to: "/diagnostico-anm", label: "Diagnóstico Inteligente", icon: Stethoscope, testid: "sidebar-diagnostico" },
    { to: "/inteligencia-geoespacial", label: "Inteligência Geoespacial", icon: Map, testid: "sidebar-geoespacial" },
    { to: "/prazos", label: "Alertas e Prazos", icon: BellRing, testid: "sidebar-prazos" },
    { to: "/assistente", label: "Assistente ANM IA", icon: Bot, testid: "sidebar-assistente" },
    { to: "/legislacao", label: "Legislação", icon: Scale, testid: "sidebar-legislacao" },
    { to: "/checklists", label: "Checklists", icon: ListChecks, testid: "sidebar-checklists" },
    { to: "/documentos", label: "Documentos", icon: FileText, testid: "sidebar-documentos" },
    { to: "/plano", label: "Assinatura", icon: CreditCard, testid: "sidebar-plano" },
    { to: "/configuracoes", label: "Configurações", icon: Cog, testid: "sidebar-configuracoes" },
    { to: "/suporte", label: "Suporte", icon: LifeBuoy, testid: "sidebar-suporte" },
];

function SidebarContent({ onNavigate, isAdmin }) {
    return (
        <div className="flex flex-col h-full bg-darkblue text-slate-300">
            <div className="px-5 py-5 border-b border-white/10">
                <NavLink to="/dashboard" onClick={onNavigate} className="flex items-center gap-2 font-heading font-black text-lg text-white" data-testid="sidebar-logo">
                    <span className="inline-flex items-center justify-center px-2 h-7 rounded bg-mineral text-white text-xs font-black shrink-0">GM² ANM</span>
                    GEOMINE MANAGER
                </NavLink>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {NAV.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onNavigate}
                        data-testid={item.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                                isActive
                                    ? "bg-petroleum text-white font-medium border-l-4 border-mineral"
                                    : "hover:bg-petroleum/60 hover:text-white border-l-4 border-transparent"
                            }`
                        }
                    >
                        <item.icon className="w-5 h-5" strokeWidth={2} />
                        {item.label}
                    </NavLink>
                ))}
                {isAdmin && (
                    <NavLink to="/admin" onClick={onNavigate} data-testid="sidebar-admin"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                                isActive ? "bg-mineral text-white font-medium" : "text-mineral hover:bg-mineral/20 border-l-4 border-transparent"
                            }`}>
                        <ShieldAlert className="w-5 h-5" /> Administração
                    </NavLink>
                )}
            </nav>
            <div className="p-4 border-t border-white/10">
                <a href="https://www.gov.br/anm/pt-br" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-slate-400 hover:text-mineral transition-colors" data-testid="sidebar-gov-link">
                    <ExternalLink className="w-4 h-4" /> Portal oficial ANM (gov.br)
                </a>
            </div>
        </div>
    );
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [notifs, setNotifs] = useState({ items: [], unread: 0 });
    const [notifOpen, setNotifOpen] = useState(false);
    const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 60000); return () => clearInterval(t); }, []);
    const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
    const notifLink = (n) => n.link || ({ prazo: "/prazos", monitoramento: "/processos", legislacao: "/legislacao", pacote: "/plano" }[n.type] || "/dashboard");
    const openNotif = async (n) => {
        setNotifOpen(false);
        try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* noop */ }
        loadNotifs();
        navigate(notifLink(n));
    };
    const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <div className="min-h-screen flex bg-background">
            <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">
                <SidebarContent isAdmin={user?.is_admin} />
            </aside>

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
                <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200">
                    <div className="flex items-center gap-3 px-4 md:px-6 h-16">
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <button className="lg:hidden p-2 -ml-2 text-slate-700" data-testid="mobile-menu-button">
                                    <Menu className="w-6 h-6" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-72 border-0">
                                <SidebarContent onNavigate={() => setMobileOpen(false)} isAdmin={user?.is_admin} />
                            </SheetContent>
                        </Sheet>

                        <div className="flex-1 max-w-xl">
                            <GlobalSearch />
                        </div>

                        <div className="flex items-center gap-3">
                            <DropdownMenu open={notifOpen} onOpenChange={(o) => { setNotifOpen(o); if (o && notifs.unread) markAll(); }}>
                                <DropdownMenuTrigger asChild>
                                    <button className="relative p-2 text-slate-600 hover:text-petroleum transition-colors" data-testid="notifications-button">
                                        <Bell className="w-5 h-5" />
                                        {notifs.unread > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-mineral text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">{notifs.unread}</span>
                                        )}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-80">
                                    <DropdownMenuLabel>Últimas notificações</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {notifs.items.length === 0 ? (
                                        <div className="px-3 py-6 text-center text-sm text-slate-400">Nenhuma notificação.</div>
                                    ) : (
                                        <div className="max-h-96 overflow-y-auto">
                                            {notifs.items.slice(0, 5).map((n) => (
                                                <button type="button" key={n.id} onClick={() => openNotif(n)}
                                                    className="w-full text-left px-3 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`notification-${n.id}`}>
                                                    <div className="text-sm font-semibold text-slate-800">{n.title}</div>
                                                    <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                                                    <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("pt-BR")}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {notifs.items.length > 5 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <button type="button" onClick={() => { setNotifOpen(false); navigate("/notificacoes"); }}
                                                className="w-full text-center px-3 py-2 text-xs font-semibold text-petroleum hover:bg-slate-50 transition-colors" data-testid="notifications-see-all">
                                                Ver todas
                                            </button>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {user?.subscription_status === "trial" && (
                                <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-mineral/15 text-mineralHover border border-mineral/30" data-testid="trial-badge">
                                    Período de teste
                                </span>
                            )}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2" data-testid="user-menu-button">
                                        <Avatar className="w-9 h-9 border border-slate-200">
                                            <AvatarImage src={user?.picture} />
                                            <AvatarFallback className="bg-petroleum text-white text-sm">{initials}</AvatarFallback>
                                        </Avatar>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>
                                        <div className="font-semibold text-slate-900">{user?.name}</div>
                                        <div className="text-xs text-slate-500 font-normal truncate">{user?.email}</div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate("/configuracoes")} data-testid="menu-settings">
                                        <Cog className="w-4 h-4 mr-2" /> Configurações
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/plano")} data-testid="menu-plan">
                                        <CreditCard className="w-4 h-4 mr-2" /> Assinatura
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" /> Sair
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                <main className="flex-1 p-4 md:p-6 lg:p-8 animate-fade-up">
                    {user?.is_member && user?.member_role === "viewer" && (
                        <div data-testid="readonly-banner" className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                            <Eye className="w-4 h-4 shrink-0" />
                            <span>Você tem acesso <strong>somente leitura</strong> nesta conta. Alterações são gerenciadas pelo titular.</span>
                        </div>
                    )}
                    {children}
                </main>

                <footer className="border-t border-slate-200 px-6 py-4 text-xs text-slate-400 bg-white">
                    Geomine Manager — plataforma privada e independente. Sem vínculo institucional com a ANM ou Governo Federal. Informações regulatórias devem ser confirmadas nas fontes oficiais.
                </footer>
            </div>
        </div>
    );
}
